import { Button } from 'blaze-ui/Button'
import Styles from './ViewYourDesign.module.scss'
import { MobileWizard } from 'components/Wizards/MobileWizards/Layout'
import { multiStep } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/multiStep'
import { useField } from 'formik'
import { observer } from 'mobx-react-lite'
import { useVisualElement } from 'hooks/queries/visualElements/useVisualElement'
import { GeneratedDesignPreview } from '../ChooseTemplate/components/GeneratedDesignPreview'
import { StepProps } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/Types'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { generatePath, useHistory } from 'react-router-dom'
import { DESIGN_PATH } from 'paths'
import { AlmanacFileSummary } from 'types'
import { useRootStore } from 'stores/RootStore'
import { useDesignDownload } from 'components/DesignDownloadModal/useDesignDownload'
import { useEffect, useState } from 'react'
import { useMobileTemplatePreviewDimensions } from 'components/Wizards/MobileWizards/hooks/useMobileTemplatePreviewDimensions'
import cx from 'classnames'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'

type Props = StepProps & {
  selectedMedia?: IMediaFile
  design?: AlmanacFileSummary
  borderRadius?: number | string
}

export const ViewYourDesign = observer(
  multiStep(({ schema, initialValues, selectedMedia, design, borderRadius }: Props) => {
    const { meta } = schema.describe()
    const history = useHistory()
    const { drawersStore } = useRootStore()
    const [downloadIntent, setDownloadIntent] = useState(false)
    const [sourceVisualElementId] = useField('source_visual_element_id')
    const [{ value: filename }] = useField('filename')
    const { width, height } = initialValues
    const { aspectRatio } = useMobileTemplatePreviewDimensions({
      width,
      height,
    })

    const {
      isDownloading,
      isLoading,
      handleSubmit: downloadImage,
      previews,
    } = useDesignDownload({ fileId: design?.id, filename })

    const previewUrl = previews[0]?.url

    const template = useVisualElement(
      { id: sourceVisualElementId.value },
      { enabled: !!sourceVisualElementId.value },
    )?.data

    const handleDownload = async () => {
      if (downloadIntent && !isLoading && !isDownloading) {
        await downloadImage({ closeOnComplete: false })
        setDownloadIntent(false)
      }
    }

    useEffect(() => {
      handleDownload()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [downloadIntent, isDownloading, isLoading])

    if (!design) {
      return <></>
    }

    const complete = () => {
      drawersStore.closeAllDrawers()
      history.push(generatePath(DESIGN_PATH, { key: design.id }))
    }

    const renderPreview = () => {
      if (template) {
        return (
          <GeneratedDesignPreview
            templateId={template.id}
            onPress={() => null}
            aspectRatio={aspectRatio}
            borderRadius={borderRadius ?? 0}
          />
        )
      }

      if (previewUrl) {
        return <img src={previewUrl} alt="" />
      }

      return <LoadingShimmerer className={Styles.loadingShimmer} borderRadius={borderRadius} />
    }

    return (
      <>
        <MobileWizard.Content>
          <MobileWizard.Section>
            <MobileWizard.Title>{meta.title}</MobileWizard.Title>
          </MobileWizard.Section>
          <div className={Styles.container}>
            <div
              className={cx(Styles.imgPreview, {
                [Styles.square]: aspectRatio === 1,
                [Styles.landscape]: aspectRatio > 1,
                [Styles.portrait]: aspectRatio < 1,
              })}
              style={{ borderRadius, aspectRatio }}
            >
              {renderPreview()}
            </div>
          </div>
        </MobileWizard.Content>

        <MobileWizard.ButtonFooter>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            isDisabled={downloadIntent}
            onPress={() => setDownloadIntent(true)}
          >
            {downloadIntent ? 'Downloading...' : 'Download'}
          </Button>
          <Button size="lg" variant="tertiary" fullWidth onPress={() => complete()}>
            Edit in Designer
          </Button>
        </MobileWizard.ButtonFooter>
      </>
    )
  }),
)
